import APIService from "service/network/APIService";
import APIConfig from "service/network/APIConfig";

/**
 * Reactions catalog — one-shot fetch of every active reaction from
 * api-allergy-v2-service. Powers BOTH the "Suggested" chip rail
 * (isCommon=true) and the searchable dropdown in the Allergy add/update
 * forms. No free-text: the reviewer must pick from this list.
 *
 * Module-level in-flight promise + cache so:
 *   • Multiple mounts on the same page share ONE HTTP call (add rail +
 *     any concurrent update sheet trigger the same request; the second
 *     caller awaits the first).
 *   • Subsequent opens on the same session return synchronously via the
 *     cache — no keystroke-driven roundtrip.
 *
 * `invalidate()` is provided for the seldom-needed refresh case (admin
 * adds a new controlled reaction mid-session and wants the picker to
 * pick it up without a full reload).
 */

let inflight = null;
let cache = null;

const doFetch = () =>
  new Promise((resolve) => {
    const svc = new APIService();
    svc.get(
      APIConfig.reactionsCatalog.list,
      (res) => {
        const list = (res && Array.isArray(res.data) ? res.data : [])
          // Defensive: server already filters isActive=true, but keep
          // the check so a mis-typed record on the wire can't slip a
          // deprecated reaction into the picker.
          .filter((r) => r && r.reaction && r.isActive !== false);
        cache = list;
        resolve(list);
      },
      // Errors: cache an empty list for this session so the UI keeps
      // working (reviewer sees an empty picker rather than an
      // infinite loading spinner) and doesn't retry per keystroke.
      // A refresh clears the cache and re-hits the endpoint.
      () => {
        cache = [];
        resolve([]);
      },
    );
  });

/** Get the full active-reactions list. Returns a Promise that resolves
    with the list. Cached — subsequent calls resolve synchronously. */
export const listReactions = () => {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = doFetch().finally(() => { inflight = null; });
  }
  return inflight;
};

/** Force the next `listReactions()` call to refetch. Useful after an
    admin-side edit to the reactionsCatalog collection. */
export const invalidate = () => {
  cache = null;
  inflight = null;
};
